import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BrainCircuit, Search, Users, BarChart, Shield, Award, ChevronRight, ArrowRight, X, Menu, CheckCircle, Zap } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const HomePage: React.FC = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  useEffect(() => {
    setIsVisible(true);
    
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);
  
  const getTranslation = (key: string, defaultText: string) => {
    try {
      // @ts-ignore - Ignore type checking for translation keys
      return t(key) || defaultText;
    } catch {
      return defaultText;
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'backdrop-blur-lg bg-white/95 shadow-md' : 'bg-transparent'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <BrainCircuit className={`h-8 w-8 ${scrolled ? 'text-indigo-600' : 'text-indigo-500'} transition-colors`} />
              <span className={`text-xl font-bold ${scrolled ? 'text-gray-900' : 'text-gray-800'} transition-colors`}>IRecruitor</span>
            </div>
            <div className="hidden md:flex items-center space-x-6">
              <a href="#features" className={`${scrolled ? 'text-gray-600' : 'text-gray-700'} hover:text-indigo-600 transition-colors font-medium`}>Features</a>
              <a href="#how-it-works" className={`${scrolled ? 'text-gray-600' : 'text-gray-700'} hover:text-indigo-600 transition-colors font-medium`}>How It Works</a>
              <a href="#pricing" className={`${scrolled ? 'text-gray-600' : 'text-gray-700'} hover:text-indigo-600 transition-colors font-medium`}>Pricing</a>
              <a href="#contact" className={`${scrolled ? 'text-gray-600' : 'text-gray-700'} hover:text-indigo-600 transition-colors font-medium`}>Contact Us</a>
              <Link to="/app" className="inline-flex items-center px-4 py-2 border border-transparent rounded-full font-medium shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 transition-all">
                Try Now <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
            <div className="md:hidden">
              <button 
                className={`${scrolled ? 'text-gray-600' : 'text-gray-700'} hover:text-indigo-600 transition-colors`}
                onClick={() => setMobileMenuOpen(true)}
              >
                <Menu className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </nav>
      
      {/* Mobile menu */}
      <div className={`fixed inset-0 z-[60] bg-white transform transition-transform duration-300 ease-in-out ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex justify-between items-center p-4 border-b">
          <div className="flex items-center space-x-2">
            <BrainCircuit className="h-8 w-8 text-indigo-600" />
            <span className="text-xl font-bold text-gray-900">IRecruitor</span>
          </div>
          <button 
            className="text-gray-500 hover:text-indigo-600"
            onClick={() => setMobileMenuOpen(false)}
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        <div className="p-4">
          <ul className="space-y-4">
            <li>
              <a 
                href="#features" 
                className="block py-2 text-lg font-medium text-gray-800 hover:text-indigo-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                Features
              </a>
            </li>
            <li>
              <a 
                href="#how-it-works" 
                className="block py-2 text-lg font-medium text-gray-800 hover:text-indigo-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                How It Works
              </a>
            </li>
            <li>
              <a 
                href="#pricing" 
                className="block py-2 text-lg font-medium text-gray-800 hover:text-indigo-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                Pricing
              </a>
            </li>
            <li>
              <a 
                href="#contact" 
                className="block py-2 text-lg font-medium text-gray-800 hover:text-indigo-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact Us
              </a>
            </li>
            <li className="pt-4">
              <Link
                to="/app"
                className="block w-full py-3 text-center rounded-full font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                Try Now
              </Link>
            </li>
          </ul>
        </div>
      </div>

      {/* Hero Section */}
      <section className="pt-28 pb-10 md:pt-28 md:pb-16 overflow-hidden relative">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute right-1/4 top-1/4 w-[500px] h-[500px] bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
          <div className="absolute left-1/4 bottom-1/4 w-[500px] h-[500px] bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-0 right-1/2 w-[500px] h-[500px] bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>
        </div>
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-6xl mx-auto">
            {/* Flex container for side-by-side layout */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between md:space-x-8">
              {/* Text Content - Left Side */}
              <div className={`w-full md:w-1/2 text-center md:text-left transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <div className="mb-4 inline-flex items-center px-4 py-2 rounded-full bg-indigo-100 text-indigo-600 text-sm font-medium">
                  <span className="block w-2 h-2 rounded-full bg-indigo-600 mr-2"></span>
                  AI-Powered Recruiting
                </div>
                <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-gradient mb-6 leading-tight">
                  Revolutionize Your Recruitment Process with AI
                </h1>
                <p className="text-xl text-gray-600 mb-8 max-w-xl leading-relaxed">
                  Our AI-driven resume analysis technology matches candidates to job descriptions with unparalleled precision, reducing hiring time by up to 75%.
                </p>
                <div className="flex flex-wrap gap-5 justify-center md:justify-start">
                  <Link
                    to="/app"
                    className="px-10 py-4 rounded-full text-lg font-semibold text-white bg-gradient-to-r from-indigo-600 to-blue-500 hover:from-indigo-500 hover:to-blue-400 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center"
                  >
                    Try It Now
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                  <a
                    href="#features"
                    className="px-10 py-4 rounded-full text-lg font-semibold text-indigo-600 bg-white border-2 border-indigo-100 hover:border-indigo-200 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                  >
                    Learn More
                  </a>
                </div>
              </div>

              {/* Image - Right Side */}
              <div className={`w-full md:w-1/2 flex justify-center mt-10 md:mt-0 transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <img
                  src="/assets/T.png"
                  alt="AI Resume Analysis"
                  className="max-w-full md:max-w-[110%] max-h-[650px] object-contain animate-float"
                  onError={(e) => {
                    e.currentTarget.src = "https://placehold.co/600x500/e0e7ff/4338ca?text=AI+Resume+Analysis"; 
                    e.currentTarget.onerror = null;
                  }}
                />
              </div>
            </div>

            {/* Trust indicators at the bottom */}
            <div className={`mt-10 md:mt-12 w-full flex flex-wrap justify-center gap-8 items-center transition-all duration-1000 delay-300 transform ${isVisible ? 'opacity-80 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5 text-indigo-600" />
                <span className="text-sm text-gray-600 font-medium">
                  Enterprise-Grade Security
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-indigo-600" />
                <span className="text-sm text-gray-600 font-medium">
                  98% Match Accuracy
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Zap className="w-5 h-5 text-indigo-600" />
                <span className="text-sm text-gray-600 font-medium">
                  Analysis in Under 60 Seconds
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent"></div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-0 top-0 w-1/3 h-1/3 bg-gradient-to-br from-indigo-50 to-transparent opacity-80"></div>
          <div className="absolute right-0 bottom-0 w-1/3 h-1/3 bg-gradient-to-tl from-blue-50 to-transparent opacity-80"></div>
          <div className="hidden lg:block absolute right-[10%] top-1/4 w-24 h-24 rounded-full border-8 border-indigo-100 opacity-50"></div>
          <div className="hidden lg:block absolute left-[10%] bottom-1/4 w-16 h-16 rounded-full border-4 border-blue-100 opacity-30"></div>
        </div>

        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-16">
            <h2 className="inline-block text-3xl md:text-4xl font-bold text-gray-900 mb-4 relative">
              Recruiting-Optimized Features
              <span className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 to-blue-500 transform translate-y-2"></span>
            </h2>
            <p className="mt-6 text-xl text-gray-600 max-w-3xl mx-auto">
              Designed specifically for HR professionals and recruiters to streamline candidate evaluation
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden">
              <div className="absolute right-0 top-0 w-24 h-24 bg-indigo-100 rounded-bl-[5rem] -mr-8 -mt-8 group-hover:bg-indigo-200 transition-colors duration-300"></div>
              <div className="relative">
                <div className="p-4 bg-indigo-100 rounded-xl w-16 h-16 flex items-center justify-center mb-6 group-hover:bg-indigo-200 transition-colors duration-300">
                  <BrainCircuit className="h-8 w-8 text-indigo-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Advanced Resume Analysis
                </h3>
                <p className="text-gray-600">
                  Our proprietary AI algorithms extract key candidate information and match it to your job requirements with over 98% accuracy.
                </p>
              </div>
            </div>
            
            {/* Feature 2 */}
            <div className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden">
              <div className="absolute right-0 top-0 w-24 h-24 bg-green-100 rounded-bl-[5rem] -mr-8 -mt-8 group-hover:bg-green-200 transition-colors duration-300"></div>
              <div className="relative">
                <div className="p-4 bg-green-100 rounded-xl w-16 h-16 flex items-center justify-center mb-6 group-hover:bg-green-200 transition-colors duration-300">
                  <Search className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Skills & Experience Matching
                </h3>
                <p className="text-gray-600">
                  Automatically identify and score candidate skills, experience levels, and qualifications against your specific job requirements.
                </p>
              </div>
            </div>
            
            {/* Feature 3 */}
            <div className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden">
              <div className="absolute right-0 top-0 w-24 h-24 bg-purple-100 rounded-bl-[5rem] -mr-8 -mt-8 group-hover:bg-purple-200 transition-colors duration-300"></div>
              <div className="relative">
                <div className="p-4 bg-purple-100 rounded-xl w-16 h-16 flex items-center justify-center mb-6 group-hover:bg-purple-200 transition-colors duration-300">
                  <Users className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Automated Candidate Ranking
                </h3>
                <p className="text-gray-600">
                  Save hours of manual screening with our intelligent ranking system that prioritizes the most qualified candidates for your position.
                </p>
              </div>
            </div>
            
            {/* Feature 4 */}
            <div className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden">
              <div className="absolute right-0 top-0 w-24 h-24 bg-amber-100 rounded-bl-[5rem] -mr-8 -mt-8 group-hover:bg-amber-200 transition-colors duration-300"></div>
              <div className="relative">
                <div className="p-4 bg-amber-100 rounded-xl w-16 h-16 flex items-center justify-center mb-6 group-hover:bg-amber-200 transition-colors duration-300">
                  <BarChart className="h-8 w-8 text-amber-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Comprehensive Analytics
                </h3>
                <p className="text-gray-600">
                  Gain valuable insights into skill gaps, experience alignment, and qualification matches for each candidate with detailed scoring.
                </p>
              </div>
            </div>
            
            {/* Feature 5 */}
            <div className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden">
              <div className="absolute right-0 top-0 w-24 h-24 bg-red-100 rounded-bl-[5rem] -mr-8 -mt-8 group-hover:bg-red-200 transition-colors duration-300"></div>
              <div className="relative">
                <div className="p-4 bg-red-100 rounded-xl w-16 h-16 flex items-center justify-center mb-6 group-hover:bg-red-200 transition-colors duration-300">
                  <Shield className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  GDPR Compliant
                </h3>
                <p className="text-gray-600">
                  All candidate data is processed securely with enterprise-grade encryption and retention controls that meet global privacy standards.
                </p>
              </div>
            </div>
            
            {/* Feature 6 */}
            <div className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden">
              <div className="absolute right-0 top-0 w-24 h-24 bg-indigo-100 rounded-bl-[5rem] -mr-8 -mt-8 group-hover:bg-indigo-200 transition-colors duration-300"></div>
              <div className="relative">
                <div className="p-4 bg-indigo-100 rounded-xl w-16 h-16 flex items-center justify-center mb-6 group-hover:bg-indigo-200 transition-colors duration-300">
                  <Award className="h-8 w-8 text-indigo-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Better Hiring Outcomes
                </h3>
                <p className="text-gray-600">
                  Organizations using our platform report 62% improvement in candidate quality and 45% reduction in time-to-hire.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <svg className="absolute right-0 top-0 h-full w-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320">
            <path fill="#f8fafc" fillOpacity="0.4" d="M0,64L48,80C96,96,192,128,288,128C384,128,480,96,576,106.7C672,117,768,171,864,176C960,181,1056,139,1152,117.3C1248,96,1344,96,1392,96L1440,96L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"></path>
          </svg>
          <svg className="absolute left-0 bottom-0 h-full w-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320">
            <path fill="#f8fafc" fillOpacity="0.4" d="M0,64L48,80C96,96,192,128,288,128C384,128,480,96,576,106.7C672,117,768,171,864,176C960,181,1056,139,1152,117.3C1248,96,1344,96,1392,96L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
          </svg>
        </div>

        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-16">
            <h2 className="inline-block text-3xl md:text-4xl font-bold text-gray-900 mb-4 relative">
              Streamlined Recruitment Process
              <span className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 to-blue-500 transform translate-y-2"></span>
            </h2>
            <p className="mt-6 text-xl text-gray-600 max-w-3xl mx-auto">
              Four simple steps to transform your hiring efficiency and candidate quality
            </p>
          </div>
          
          <div className="max-w-5xl mx-auto relative">
            {/* Central timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-indigo-500 to-blue-500 hidden md:block"></div>
            
            <div className="space-y-12 md:space-y-32 relative">
              {/* Step 1 */}
              <div className="flex flex-col md:flex-row items-center">
                <div className="md:w-1/2 md:pr-16 mb-8 md:mb-0 text-center md:text-right">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Define Your Job Requirements
                  </h3>
                  <p className="text-gray-600">
                    Input your job title, description, required skills, qualifications, and experience levels to create a precise matching profile.
                  </p>
                </div>
                <div className="relative flex items-center justify-center z-10">
                  <div className="w-16 h-16 rounded-full bg-white shadow-xl border-4 border-indigo-500 flex items-center justify-center text-xl font-bold text-indigo-600">
                    1
                  </div>
                </div>
                <div className="md:w-1/2 md:pl-16 hidden md:block">
                  <div className="transform hover:scale-105 transition-transform duration-300 bg-white shadow-lg rounded-xl p-4 border border-gray-100">
                    <img 
                      src="https://placehold.co/400x300/e0e7ff/4338ca?text=Job+Description" 
                      alt="Upload Job Description" 
                      className="w-full rounded-lg" 
                    />
                  </div>
                </div>
              </div>
              
              {/* Step 2 */}
              <div className="flex flex-col md:flex-row-reverse items-center">
                <div className="md:w-1/2 md:pl-16 mb-8 md:mb-0 text-center md:text-left">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Upload Candidate Resumes
                  </h3>
                  <p className="text-gray-600">
                    Upload multiple resumes in PDF format simultaneously. Our advanced parsing technology extracts all relevant candidate information.
                  </p>
                </div>
                <div className="relative flex items-center justify-center z-10">
                  <div className="w-16 h-16 rounded-full bg-white shadow-xl border-4 border-indigo-500 flex items-center justify-center text-xl font-bold text-indigo-600">
                    2
                  </div>
                </div>
                <div className="md:w-1/2 md:pr-16 hidden md:block">
                  <div className="transform hover:scale-105 transition-transform duration-300 bg-white shadow-lg rounded-xl p-4 border border-gray-100">
                    <img 
                      src="https://placehold.co/400x300/e0e7ff/4338ca?text=Upload+Resumes" 
                      alt="Upload Resumes" 
                      className="w-full rounded-lg" 
                    />
                  </div>
                </div>
              </div>
              
              {/* Step 3 */}
              <div className="flex flex-col md:flex-row items-center">
                <div className="md:w-1/2 md:pr-16 mb-8 md:mb-0 text-center md:text-right">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    AI-Powered Analysis
                  </h3>
                  <p className="text-gray-600">
                    Our proprietary algorithms analyze each resume, scoring candidates on skills match, experience relevance, qualification alignment, and overall fit.
                  </p>
                </div>
                <div className="relative flex items-center justify-center z-10">
                  <div className="w-16 h-16 rounded-full bg-white shadow-xl border-4 border-indigo-500 flex items-center justify-center text-xl font-bold text-indigo-600">
                    3
                  </div>
                </div>
                <div className="md:w-1/2 md:pl-16 hidden md:block">
                  <div className="transform hover:scale-105 transition-transform duration-300 bg-white shadow-lg rounded-xl p-4 border border-gray-100">
                    <img 
                      src="https://placehold.co/400x300/e0e7ff/4338ca?text=AI+Analysis" 
                      alt="AI Analysis" 
                      className="w-full rounded-lg" 
                    />
                  </div>
                </div>
              </div>
              
              {/* Step 4 */}
              <div className="flex flex-col md:flex-row-reverse items-center">
                <div className="md:w-1/2 md:pl-16 mb-8 md:mb-0 text-center md:text-left">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Review Prioritized Results
                  </h3>
                  <p className="text-gray-600">
                    Get instant access to ranked candidates with detailed match scores across key categories, comprehensive analysis, and highlighted strengths and gaps.
                  </p>
                </div>
                <div className="relative flex items-center justify-center z-10">
                  <div className="w-16 h-16 rounded-full bg-white shadow-xl border-4 border-indigo-500 flex items-center justify-center text-xl font-bold text-indigo-600">
                    4
                  </div>
                </div>
                <div className="md:w-1/2 md:pr-16 hidden md:block">
                  <div className="transform hover:scale-105 transition-transform duration-300 bg-white shadow-lg rounded-xl p-4 border border-gray-100">
                    <img 
                      src="https://placehold.co/400x300/e0e7ff/4338ca?text=Review+Results" 
                      alt="Review Results" 
                      className="w-full rounded-lg" 
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-20 text-center">
              <Link
                to="/app"
                className="inline-flex items-center px-10 py-5 rounded-full text-lg font-semibold text-white bg-gradient-to-r from-indigo-600 to-blue-500 hover:from-indigo-500 hover:to-blue-400 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                Transform Your Hiring Process
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-blue-600 to-indigo-800"></div>
        <div className="absolute inset-0 opacity-10 bg-[url('/assets/bg-pattern.svg')]"></div>
        
        {/* Decorative elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute right-[10%] top-1/4 w-64 h-64 rounded-full bg-white opacity-10 mix-blend-overlay filter blur-3xl"></div>
          <div className="absolute left-[10%] bottom-1/4 w-64 h-64 rounded-full bg-white opacity-10 mix-blend-overlay filter blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-4 inline-block">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white bg-opacity-20 backdrop-blur-sm text-white text-sm font-medium">
                <span className="block w-3 h-3 rounded-full bg-green-400 mr-2 animate-pulse"></span>
                Used by 1000+ Companies
              </div>
            </div>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              Ready to Find Your Ideal Candidates Faster?
            </h2>
            <p className="text-xl mb-10 text-blue-100 max-w-3xl mx-auto">
              Join industry leaders who have reduced hiring time by 75% and improved candidate quality by 62% using our AI-powered resume analysis platform.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/app"
                className="px-8 py-4 rounded-full bg-white text-indigo-700 font-bold text-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center"
              >
                Start Finding Better Candidates
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <a
                href="#how-it-works"
                className="px-8 py-4 rounded-full bg-transparent border-2 border-white text-white font-bold text-lg hover:bg-white/10 transition-all duration-300 flex items-center justify-center"
              >
                Learn More
              </a>
            </div>
            
            <div className="mt-12 flex flex-wrap justify-center gap-8">
              <div className="flex items-center text-white">
                <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>No credit card required</span>
              </div>
              <div className="flex items-center text-white">
                <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Free plan available</span>
              </div>
              <div className="flex items-center text-white">
                <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Cancel anytime</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300">
        <div className="container mx-auto px-4">
          {/* Main footer content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 py-16">
            <div>
              <div className="flex items-center space-x-2 mb-6">
                <BrainCircuit className="h-7 w-7 text-indigo-400" />
                <span className="text-xl font-bold text-white">IRecruitor</span>
              </div>
              <p className="text-gray-400 mb-6 max-w-xs">
                IRecruitor helps HR teams and hiring managers save valuable time and find better candidates with our award-winning AI resume analysis technology.
              </p>
              <div className="flex space-x-4">
                {/* Social media icons */}
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z"/></svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723 9.99 9.99 0 01-3.127 1.195 4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-2 16h-2v-6h2v6zm-1-6.891c-.607 0-1.1-.496-1.1-1.109 0-.612.492-1.109 1.1-1.109s1.1.497 1.1 1.109c0 .613-.493 1.109-1.1 1.109zm8 6.891h-1.998v-2.861c0-1.881-2.002-1.722-2.002 0v2.861h-2v-6h2v1.093c.872-1.616 4-1.736 4 1.548v3.359z"/></svg>
                </a>
              </div>
            </div>
            
            <div>
              <h3 className="text-white text-lg font-bold mb-6">Product</h3>
              <ul className="space-y-4">
                <li><a href="#features" className="text-gray-400 hover:text-white transition-colors">Features</a></li>
                <li><a href="#how-it-works" className="text-gray-400 hover:text-white transition-colors">How It Works</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Testimonials</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-white text-lg font-bold mb-6">Resources</h3>
              <ul className="space-y-4">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Support</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">API</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-white text-lg font-bold mb-6">Company</h3>
              <ul className="space-y-4">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">About Us</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">
                  Privacy Policy
                </a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">
                  Terms of Service
                </a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">
                  Contact Us
                </a></li>
              </ul>
            </div>
          </div>
          
          {/* Bottom bar */}
          <div className="border-t border-gray-800 py-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400">
              © 2023 IRecruitor. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <a href="#" className="text-gray-400 hover:text-white transition-colors">Terms</a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">Privacy</a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">Cookies</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}; 